import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import { useNotification } from '../../../../context/NotificationContext';
import GoogleLoginButton from '../../../../components/GoogleLoginButton';
import AppleLoginButton from '../../../../components/AppleLoginButton';


const ClinicRegister = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    clinic_name: '',
    license_number: '',

    // Address Details
    street: '',
    locality: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    landmark: '',
    map_location: '',

    // Operations
    specialties: '',
    website: '',
    landline: '',
    opening_time: '09:00',
    closing_time: '21:00',
    services: '', // Comma separated

    terms: false
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Contact person name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Confirm password is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.clinic_name.trim()) newErrors.clinic_name = 'Clinic name is required';
    if (!formData.license_number.trim()) newErrors.license_number = 'License number is required';

    if (!formData.street.trim()) newErrors.street = 'Street address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.pincode.trim()) newErrors.pincode = 'Pincode is required';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    if (formData.phone && !phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Invalid Indian phone number';
    }

    if (formData.pincode && (formData.pincode.length !== 6 || !/^\d+$/.test(formData.pincode))) {
      newErrors.pincode = 'Invalid pincode';
    }

    if (!formData.terms) newErrors.terms = 'You must accept the Terms and Conditions';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showError('Please fix the validation errors');
      return;
    }

    setLoading(true);

    try {
      // Structure data for backend
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        password: formData.password,
        role: 'clinic',

        // Organization Details Mapping
        organization_details: {
          logo: '', // Handle upload separately if needed
          landline: formData.landline,
          website: formData.website,
          address: {
            street: formData.street,
            locality: formData.locality,
            city: formData.city,
            state: formData.state,
            country: formData.country,
            zipcode: formData.pincode,
            landmark: formData.landmark,
            map_location: formData.map_location
          },
          business_hours: [{
            day: 'Mon-Sun', // Simplified for MV, can be detailed later
            openTime: formData.opening_time,
            closeTime: formData.closing_time
          }],
          services: formData.services.split(',').map(s => s.trim()).filter(s => s),
          timings_display: `${formData.opening_time} - ${formData.closing_time}`,
          compliance: {
            // Documents to be uploaded in "Document Upload" step after registration
            ownership_proof: '',
            registration_proof: '',
            corporation_cert: '',
            waste_cert: '',
            gst_cert: ''
          }
        },
        // Legacy/Compat fields
        clinic_name: formData.clinic_name.trim(),
        license_number: formData.license_number.trim(),
        address: `${formData.street}, ${formData.locality}, ${formData.city}`,
        city: formData.city.trim(),
        pincode: formData.pincode.trim(),
        specialties: formData.specialties.trim(),
      };

      const result = await register('clinic', payload);

      if (result.success) {
        showSuccess('Clinic registration submitted! Please upload documents in the next step.');
        localStorage.setItem('temp_userId', result.user._id);
        navigate(`/onboarding/documents?userId=${result.user._id}&role=clinic`); // Redirect to document upload
      } else {
        showError(result.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      const message = error.response?.data?.message || 'Network error. Please try again.';
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark flex items-center justify-center p-4">
      <div className="w-full max-w-3xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gold mb-2">Dr. Vivara</h1>
          <p className="text-light">Clinic Registration</p>
          <p className="text-gray-400 text-sm mt-2">Comprehensive Setup for Your Medical Practice</p>
        </div>

        <div className="bg-gray-800 rounded-xl p-6">
          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Section 1: Basic Info */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gold border-b border-gray-700 pb-2">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 mb-2">Clinic Name *</label>
                  <input type="text" name="clinic_name" value={formData.clinic_name} onChange={handleChange} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light" placeholder="Clinic Name" />
                  {errors.clinic_name && <p className="text-red-500 text-sm">{errors.clinic_name}</p>}
                </div>
                <div>
                  <label className="block text-gray-400 mb-2">License Number (KPME) *</label>
                  <input type="text" name="license_number" value={formData.license_number} onChange={handleChange} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light" placeholder="License Number" />
                  {errors.license_number && <p className="text-red-500 text-sm">{errors.license_number}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 mb-2">Administrator Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light" />
                  {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-gray-400 mb-2">Email *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light" />
                  {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 mb-2">Phone *</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light" maxLength="10" />
                  {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
                </div>
                <div>
                  <label className="block text-gray-400 mb-2">Landline (Optional)</label>
                  <input type="tel" name="landline" value={formData.landline} onChange={handleChange} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light" />
                </div>
              </div>
            </div>

            {/* Section 2: Address & Location */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gold border-b border-gray-700 pb-2">Location Details</h3>
              <div>
                <label className="block text-gray-400 mb-2">Street Address *</label>
                <textarea name="street" value={formData.street} onChange={handleChange} rows="2" className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light" />
                {errors.street && <p className="text-red-500 text-sm">{errors.street}</p>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 mb-2">Locality/Area</label>
                  <input type="text" name="locality" value={formData.locality} onChange={handleChange} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light" />
                </div>
                <div>
                  <label className="block text-gray-400 mb-2">Landmark</label>
                  <input type="text" name="landmark" value={formData.landmark} onChange={handleChange} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-400 mb-2">City *</label>
                  <input type="text" name="city" value={formData.city} onChange={handleChange} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light" />
                  {errors.city && <p className="text-red-500 text-sm">{errors.city}</p>}
                </div>
                <div>
                  <label className="block text-gray-400 mb-2">State</label>
                  <input type="text" name="state" value={formData.state} onChange={handleChange} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light" />
                </div>
                <div>
                  <label className="block text-gray-400 mb-2">Pincode *</label>
                  <input type="text" name="pincode" value={formData.pincode} onChange={handleChange} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light" maxLength="6" />
                  {errors.pincode && <p className="text-red-500 text-sm">{errors.pincode}</p>}
                </div>
              </div>
              <div>
                <label className="block text-gray-400 mb-2">Google Maps Link</label>
                <input type="text" name="map_location" value={formData.map_location} onChange={handleChange} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light" placeholder="https://goo.gl/maps/..." />
              </div>
            </div>

            {/* Section 3: Operations */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gold border-b border-gray-700 pb-2">Business Operations</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 mb-2">Opening Time</label>
                  <input type="time" name="opening_time" value={formData.opening_time} onChange={handleChange} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light" />
                </div>
                <div>
                  <label className="block text-gray-400 mb-2">Closing Time</label>
                  <input type="time" name="closing_time" value={formData.closing_time} onChange={handleChange} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light" />
                </div>
              </div>
              <div>
                <label className="block text-gray-400 mb-2">Services (Comma separated)</label>
                <input type="text" name="services" value={formData.services} onChange={handleChange} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light" placeholder="e.g. X-Ray, Blood Test, Consultation" />
              </div>
              <div>
                <label className="block text-gray-400 mb-2">Website (Optional)</label>
                <input type="url" name="website" value={formData.website} onChange={handleChange} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light" placeholder="https://..." />
              </div>
            </div>

            {/* Section 4: Security */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gold border-b border-gray-700 pb-2">Account Security</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 mb-2">Password *</label>
                  <div className="relative">
                    <input type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleChange} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 pr-10 text-light focus:outline-none focus:ring-2 focus:ring-gold" />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gold transition-colors focus:outline-none"
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-sm">{errors.password}</p>}
                </div>
                <div>
                  <label className="block text-gray-400 mb-2">Confirm Password *</label>
                  <div className="relative">
                    <input type={showConfirmPassword ? "text" : "password"} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 pr-10 text-light focus:outline-none focus:ring-2 focus:ring-gold" />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gold transition-colors focus:outline-none"
                    >
                      {showConfirmPassword ? (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && <p className="text-red-500 text-sm">{errors.confirmPassword}</p>}
                </div>
              </div>
            </div>

            <div className="bg-gray-900 rounded-lg p-4">
              <h3 className="text-gold font-semibold mb-2">Next Steps: Verification</h3>
              <p className="text-gray-400 text-sm">
                After registration, you will be asked to upload the following proofs:
                Ownership, KPME Registration, Corporation Cert, Biomedical Waste Cert, and GST.
                Your account will remain <strong>Pending</strong> until Admin verification.
              </p>
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  checked={formData.terms}
                  onChange={(e) => setFormData({ ...formData, terms: e.target.checked })}
                  className="focus:ring-gold h-4 w-4 text-gold border-gray-600 rounded bg-gray-700"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="terms" className="font-medium text-gray-300">
                  I agree to the{' '}
                  <Link to="/terms" className="text-gold hover:underline">
                    Terms and Conditions
                  </Link>
                  ,{' '}
                  <Link to="/privacy" className="text-gold hover:underline">
                    Privacy Policy
                  </Link>
                  , and{' '}
                  <Link to="/support" className="text-gold hover:underline">
                    Support
                  </Link>
                </label>
                {errors.terms && <p className="text-red-500 text-sm mt-1">{errors.terms}</p>}
              </div>
            </div>

            <button type="submit" disabled={loading} className="w-full bg-gold text-dark py-3 rounded-lg font-medium disabled:opacity-50 hover:opacity-90">
              {loading ? 'Submitting...' : 'Submit & Proceed to Uploads'}
            </button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-800 text-gray-500">Or register with</span>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <GoogleLoginButton role="clinic" onSuccess={(user) => navigate('/onboarding/documents?userId=' + user._id + '&role=clinic')} />
              <AppleLoginButton role="clinic" onSuccess={(user) => navigate('/onboarding/documents?userId=' + user._id + '&role=clinic')} />
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-400">
              Already have an account? <button onClick={() => navigate('/auth/clinic/login')} className="text-gold hover:underline">Sign In</button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicRegister;
